import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Dumbbell, ScanLine, UserCircle2, Activity, ChevronRight, ShoppingBag } from "lucide-react";
import { apiClient } from "../../services/api";
import { useAuthStore } from "../../store/authStore";
import TopBar from "../../components/layout/TopBar";
import BottomNav from "../../components/layout/BottomNav";
import { IMG } from "../../utils/images";
import AICoachCard from "../../components/personal-ai-trainer/AICoachCard";
import FitScoreHero from "../../components/home/FitScoreHero";
import MorningCheckinCard from "../../components/home/MorningCheckinCard";
import HydrationRow from "../../components/home/HydrationRow";
import RoleBadge from "../../components/home/RoleBadge";
import useArenaStatus from "../../hooks/useArenaStatus";
import { ArenaTopBar, ArenaHomeTile, ArenaScannerLauncher } from "../../components/arena/HomeArena";

export default function Home() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [insight, setInsight] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [communities, setCommunities] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [shopRec, setShopRec] = useState(null);
  const [fpBalance, setFpBalance] = useState(user?.fitpoints_balance ?? 0);
  const [calorieGoal, setCalorieGoal] = useState(null); // server-computed
  const [streak, setStreak] = useState(null);
  const [hasMealToday, setHasMealToday] = useState(null); // null=loading, true/false=known
  const [rolesData, setRolesData] = useState(null); // { primary, roles, pending, detail }
  const recFetchedRef = useRef(false);
  const recSeenRef = useRef(false);

  // Auto-redirect superadmins to /superadmin on first visit per session.
  // sessionStorage flag lets them manually navigate back to /home if they want
  // to view the consumer experience.
  useEffect(() => {
    if (user?.is_superadmin && !sessionStorage.getItem("superadmin_visited")) {
      sessionStorage.setItem("superadmin_visited", "1");
      navigate("/superadmin", { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    apiClient.get("/me/insight").then((r) => setInsight(r.data.insight)).catch(() => setInsight("Stay consistent today — even 30 minutes counts."));
    apiClient.get("/me/history?days=7").then((r) => setDashboard(r.data)).catch(() => {});
    apiClient.get("/communities").then((r) => setCommunities(r.data)).catch(() => {});
    apiClient.get("/challenges").then((r) => setChallenges(r.data)).catch(() => {});
    // Session 3: server-authoritative calorie goal + live FP balance.
    apiClient.get("/users/me/calorie-goal").then((r) => setCalorieGoal(r.data)).catch(() => setCalorieGoal(null));
    apiClient.get("/fitpoints/balance").then((r) => setFpBalance(r.data?.balance ?? 0)).catch(() => {});
    apiClient.get("/me/roles").then((r) => setRolesData(r.data)).catch(() => setRolesData(null));
    // Session 6.5 — unified streak (server-side counted, no client math).
    apiClient.get("/users/me/streak")
      .then((r) => setStreak(r.data?.current_streak ?? 0))
      .catch(() => setStreak(0));
    // Empty-state detection for the "Log your first meal" quick card. If
    // the dashboard endpoint reports today's `consumed_kcal` > 0 (or any
    // meals logged), we hide the CTA. `hasMealToday` starts null so the
    // card doesn't flash while data is loading.
    apiClient.get("/me/history?days=1")
      .then((r) => {
        const todayKcal = r.data?.calorie_chart?.[r.data.calorie_chart.length - 1]?.calories ?? 0;
        setHasMealToday(todayKcal > 0);
      })
      .catch(() => setHasMealToday(false));
    // Guard against React StrictMode's double-invoke so we don't hit the server twice
    if (recFetchedRef.current) return;
    recFetchedRef.current = true;
    apiClient.get("/marketplace/context/home-rec").then((r) => setShopRec(r.data)).catch(() => setShopRec(null));
  }, []);

  // Mark the card as "seen" once — drives the 3-day cooldown on the server
  useEffect(() => {
    if (!shopRec?.should_show || !shopRec?.product) return;
    if (recSeenRef.current) return;
    recSeenRef.current = true;
    apiClient.post("/marketplace/context/home-rec/seen").catch(() => {});
  }, [shopRec]);

  const greet = greeting();
  // Session 3: goal comes from the server, never computed client-side.
  const calorieTarget = calorieGoal?.calorie_goal ?? 2000;
  // Session 6: no hardcoded step fallbacks. Show whatever the server has,
  // or dashes when absent — never fake numbers.
  const todaySteps = dashboard?.heatmap?.[dashboard.heatmap.length - 1]?.steps ?? null;
  const consumedCal = dashboard?.calorie_chart?.[dashboard.calorie_chart.length - 1]?.calories ?? 0;
  const remainingCal = Math.max(0, Math.round(calorieTarget - consumedCal));
  const activeMins = dashboard?.today?.active_minutes ?? null;

  // Arena status — drives the gold mode bar + tile + scanner button
  const arena = useArenaStatus();
  const inArenaMode = !!arena.active_checkin;
  const arenaGym = arena.gym;

  return (
    <div className="app-page" data-testid="home-page">
      {inArenaMode && <ArenaTopBar gymName={arenaGym?.gym_name} />}
      <TopBar right={<ArenaScannerLauncher />} />
      <main className="max-w-2xl mx-auto px-4 pt-2 pb-8 space-y-6">
        {/* Greeting + FitPoints chip */}
        <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display text-[28px] leading-tight">
                {greet}, {(user?.name || "there").split(" ")[0]}
              </h1>
              {rolesData?.primary && <RoleBadge role={rolesData.primary} size="sm" />}
            </div>
            <p className="text-[13px] text-ink-soft mt-0.5">Here&apos;s your training pulse for today.</p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/me/journey")}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-pill text-[13px] font-medium bg-canvas2 border border-line2"
            style={{ borderRadius: 100 }}
            data-testid="home-streak-chip"
            aria-label={`${streak ?? 0} day streak — view fitness journey`}
          >
            <span style={{ fontSize: 12 }}>🔥</span>
            <span className="text-ink">{streak ?? 0}</span>
            <span className="text-ink-soft">day streak</span>
          </button>
        </motion.section>

        {/* Session 3 — FitScore hero + Next Action card (server-authoritative,
            two cards stacked, mobile-spec aligned). */}
        <FitScoreHero />

        {/* Session 3 — Morning check-in (05:00–11:00 IST, hidden if done) */}
        <MorningCheckinCard />

        {/* Hydration moved to Nutrition page per updated spec. */}
        {/* Arena tile — only shown to linked members */}
        {arenaGym && !inArenaMode && (
          <>
            <ArenaHomeTile gym={arenaGym} />
            <button
              onClick={() => navigate("/me/journey")}
              data-testid="home-view-journey"
              style={{
                marginTop: -6, padding: "10px 14px", width: "100%",
                background: "transparent", border: "1px dashed rgba(12,12,11,0.18)",
                borderRadius: 12, fontFamily: "Barlow", fontSize: 12, fontWeight: 700,
                color: "var(--text-secondary, #4A4845)", textAlign: "left",
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}
            >
              <span>View my fitness journey</span>
              <span style={{ color: "#A37B00" }}>→</span>
            </button>
          </>
        )}

        {/* Discover Arena — shown to NON-linked members so they can find a partner gym */}
        {!arenaGym && !inArenaMode && (
          <button
            onClick={() => navigate("/arena/find")}
            data-testid="home-discover-arena"
            style={{
              position: "relative", overflow: "hidden",
              padding: "14px 16px", width: "100%", textAlign: "left",
              background: "linear-gradient(135deg, #0C0C0B 0%, #1A1A18 100%)",
              border: "1px solid rgba(245,197,24,0.35)", borderRadius: 16,
              color: "white", display: "flex", alignItems: "center", gap: 12,
            }}
          >
            <div style={{
              width: 38, height: 38, borderRadius: "50%",
              background: "rgba(245,197,24,0.12)",
              border: "1px solid rgba(245,197,24,0.40)",
              display: "grid", placeItems: "center", flexShrink: 0,
            }}>
              <span style={{ fontFamily: "Barlow Condensed", fontWeight: 900, color: "#F5C518", fontSize: 20 }}>A</span>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#F5C518", textTransform: "uppercase", letterSpacing: "0.10em" }}>FitFrik Arena</div>
              <div style={{ fontFamily: "Barlow Condensed", fontSize: 18, fontWeight: 800, lineHeight: 1.05, marginTop: 2 }}>Find a partner gym near you</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.62)", marginTop: 2 }}>Link your gym · earn FitPoints · 3-second check-in</div>
            </div>
            <span style={{ color: "#F5C518", fontSize: 18, fontWeight: 700 }}>→</span>
          </button>
        )}

        {/* Trainer note today (in Arena Mode) */}
        {inArenaMode && arena.trainer_note_today && (
          <section data-testid="arena-trainer-note" style={{ padding: 14, background: "var(--bg-surface, #E4E2DE)", border: "1px solid rgba(245,197,24,0.4)", borderLeft: "3px solid #F5C518", borderRadius: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#A37B00" }}>Note from your trainer</div>
            <p style={{ fontFamily: "Barlow", fontSize: 14, fontWeight: 500, color: "#0C0C0B", marginTop: 4 }}>&ldquo;{arena.trainer_note_today.note_text}&rdquo;</p>
            <div style={{ fontSize: 11, color: "var(--text-tertiary, #8A8884)", marginTop: 4 }}>— {arena.trainer_note_today.trainer_name || "Your trainer"}</div>
          </section>
        )}

        {/* Personal AI Trainer Coach card */}
        <AICoachCard />

        {/* Nutritionist-set target banner — only visible when an active
            nutrition plan is overriding the computed Mifflin goal. */}
        {calorieGoal?.basis === "nutritionist_plan" && (
          <section
            data-testid="nutritionist-target-banner"
            style={{
              padding: "10px 14px",
              background: "#DCF6F2",
              borderLeft: "3px solid #0F766E",
              borderRadius: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            <div>
              <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em", color: "#0F766E" }}>
                Target set by {calorieGoal.set_by || "your nutritionist"}
              </div>
              <div style={{ fontFamily: "Barlow", fontSize: 12.5, color: "#0C0C0B", marginTop: 2 }}>
                {calorieGoal.plan_name || "Meal plan"} · {calorieGoal.calorie_goal} kcal · {calorieGoal.protein_g || 0}g P · {calorieGoal.carbs_g || 0}g C · {calorieGoal.fat_g || 0}g F
              </div>
            </div>
            <button
              onClick={() => navigate("/me/plans")}
              data-testid="view-plan-btn"
              style={{ fontSize: 11, fontWeight: 700, color: "#0F766E", background: "transparent", border: "none", textDecoration: "underline" }}
            >
              View plan
            </button>
          </section>
        )}

        {/* Today's summary strip — three tap-through cards.
            Card 1: server-computed calorie goal → nutrition (log or edit).
            Card 2: steps → integrations screen where devices connect.
            Card 3: active minutes → workout history. */}
        <section className="grid grid-cols-3 gap-3" data-testid="today-summary">
          <SummaryCard
            testid="summary-calorie-goal"
            label="Goal"
            value={calorieTarget.toLocaleString("en-IN")}
            unit={hasMealToday === false ? "Log your first meal" : "kcal target"}
            onClick={() =>
              navigate(hasMealToday === false ? "/nutrition/scanner" : "/nutrition")
            }
          />
          <SummaryCard
            testid="summary-steps"
            label="Steps"
            value={todaySteps == null ? "—" : todaySteps.toLocaleString("en-IN")}
            unit="today"
            onClick={() => navigate("/settings/devices")}
          />
          <SummaryCard
            testid="summary-active"
            label="Active"
            value={activeMins == null ? "—" : activeMins}
            unit="min"
            onClick={() => navigate("/workouts")}
          />
        </section>

        {/* AI Coach Insight */}
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.05 }}
          className="bg-white rounded-[16px] p-4 border border-line2 relative overflow-hidden"
          style={{ boxShadow: "var(--shadow-card)" }}
          data-testid="ai-coach-insight"
        >
          <div className="absolute top-0 left-0 bottom-0 w-[3px]" style={{ background: "var(--accent-blue)" }} />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 grid place-items-center rounded-full" style={{ background: "var(--accent-blue-dim)" }}>
                <Sparkles size={14} style={{ color: "var(--accent-blue)" }} />
              </div>
              <span className="text-[11px] uppercase tracking-wider text-ink-soft">Your AI Coach</span>
            </div>
            <button
              onClick={() => navigate("/nutrition/chat")}
              className="text-[13px] text-ink font-medium flex items-center gap-1"
            >
              Chat <ChevronRight size={14} />
            </button>
          </div>
          <p className="mt-2.5 text-[15px] text-ink leading-relaxed">{insight || "Loading your insight…"}</p>
        </motion.section>

        {/* Today's workout hero */}
        <section className="relative h-[200px] rounded-[16px] overflow-hidden" data-testid="today-workout-card">
          <img src={IMG.todayWorkout} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.75) 100%)" }} />
          <div className="absolute top-3 left-3">
            <span className="px-2.5 py-1 rounded-pill text-[11px] font-medium" style={{ background: "var(--accent-blue)", color: "#000", borderRadius: 100 }}>
              AI Generated
            </span>
          </div>
          <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between text-white">
            <div className="min-w-0 pr-3">
              <h3 className="font-display text-[24px] leading-tight">Upper Body Strength</h3>
              <p className="text-[12px] text-white/80 mt-1">45 min &middot; 7 exercises &middot; Intermediate</p>
            </div>
            <button
              onClick={() => navigate("/workouts/gym?tab=generate")}
              data-testid="start-todays-workout"
              className="shrink-0 px-4 h-11 rounded-pill text-[14px] font-medium grid place-items-center"
              style={{ background: "var(--accent-blue)", color: "#000", borderRadius: 100 }}
            >
              Start
            </button>
          </div>
        </section>

        {/* Smart Shop Rec — appears below workout hero, max once per 3 days */}
        {shopRec?.should_show && shopRec.product && (
          <button
            type="button"
            onClick={() => navigate(`/marketplace/product/${shopRec.product.product_id}`)}
            data-testid="home-shop-rec"
            className="w-full text-left bg-white rounded-[16px] p-3 border border-line2 flex items-center gap-3"
            style={{ boxShadow: "var(--shadow-card)" }}
          >
            <div
              className="w-14 h-14 rounded-[12px] overflow-hidden shrink-0 bg-canvas2 grid place-items-center"
            >
              {(shopRec.product.photos || [])[0] ? (
                <img src={shopRec.product.photos[0]} alt="" className="w-full h-full object-cover" />
              ) : (
                <ShoppingBag size={20} className="text-ink-tertiary" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <ShoppingBag size={12} style={{ color: "#00B0D5" }} />
                <span
                  className="text-[10px] uppercase tracking-[0.12em] font-bold"
                  style={{ color: "#00B0D5" }}
                >
                  From the Shop
                </span>
              </div>
              <div className="text-[14px] font-semibold leading-tight mt-0.5 line-clamp-1">
                {shopRec.product.title}
              </div>
              <div className="text-[11px] text-ink-soft mt-0.5 line-clamp-1">
                {shopRec.reason} · ₹{(shopRec.product.price_inr || 0).toLocaleString("en-IN")}
              </div>
            </div>
            <ChevronRight size={18} className="text-ink-tertiary shrink-0" />
          </button>
        )}

        {/* Quick actions 2x2 (or 2x3 when not linked to Arena) */}
        <section className="grid grid-cols-2 gap-3" data-testid="quick-actions">
          <QuickTile to="/nutrition/scanner" img={IMG.scanFood} label="Scan Food" icon={ScanLine} />
          <QuickTile to="/trainers" img={IMG.trainerTile} label="Book Trainer" icon={UserCircle2} />
          <QuickTile to="/health" img={IMG.bodyScan} label="Body Stats" icon={Activity} />
          <QuickTile to="/community" img={IMG.community} label="Community" icon={Dumbbell} />
          {!arenaGym && (
            <button
              onClick={() => navigate("/arena/find")}
              data-testid="qa-find-gym"
              className="relative overflow-hidden rounded-[16px] h-[110px] text-left col-span-2"
              style={{
                background: "linear-gradient(135deg, #0C0C0B 0%, #1A1A18 100%)",
                border: "1px solid rgba(245,197,24,0.35)",
                color: "white", padding: 14,
                display: "flex", alignItems: "center", gap: 12,
              }}
            >
              <div style={{
                width: 42, height: 42, borderRadius: "50%",
                background: "rgba(245,197,24,0.14)",
                border: "1px solid rgba(245,197,24,0.45)",
                display: "grid", placeItems: "center", flexShrink: 0,
              }}>
                <span style={{ fontFamily: "Barlow Condensed", fontWeight: 900, color: "#F5C518", fontSize: 22 }}>A</span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#F5C518", textTransform: "uppercase", letterSpacing: "0.10em" }}>Arena</div>
                <div style={{ fontFamily: "Barlow Condensed", fontSize: 18, fontWeight: 800, lineHeight: 1.05, marginTop: 2 }}>Find a partner gym</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.62)", marginTop: 2 }}>3-second check-in · earn FitPoints</div>
              </div>
              <span style={{ color: "#F5C518", fontSize: 18, fontWeight: 700, flexShrink: 0 }}>→</span>
            </button>
          )}
        </section>

        {/* Near You */}
        <section data-testid="near-you">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-[22px]">Near You</h2>
          </div>
          <div className="flex gap-3 overflow-x-auto -mx-4 px-4 snap-x">
            {communities.slice(0, 3).map((c) => (
              <div
                key={c.community_id}
                className="snap-start min-w-[280px] h-[160px] rounded-[16px] relative overflow-hidden border border-line2"
                style={{ boxShadow: "var(--shadow-soft)" }}
                onClick={() => navigate("/community")}
              >
                <img src={c.cover_photo_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.75) 100%)" }} />
                <div className="absolute bottom-3 left-3 right-3 text-white">
                  <div className="font-display text-[18px]">{c.name}</div>
                  <div className="text-[12px] text-white/80 mt-0.5">
                    {(c.member_count ?? 0).toLocaleString("en-IN")} members &middot; {c.city}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Active challenges */}
        <section data-testid="active-challenges">
          <h2 className="font-display text-[22px] mb-3">Active Challenges</h2>
          <div className="space-y-3">
            {challenges.slice(0, 2).map((c) => (
              <div key={c.challenge_id} className="bg-white rounded-[16px] p-4 border border-line2 flex gap-3" style={{ boxShadow: "var(--shadow-soft)" }}>
                <div className="w-16 h-16 rounded-[12px] overflow-hidden shrink-0">
                  <img src={c.cover_photo_url} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-[16px] leading-tight">{c.title}</div>
                  <div className="text-[12px] text-ink-soft mt-0.5">
                    {(c.participant_count ?? 0).toLocaleString("en-IN")} joined &middot; {c.duration_days} days
                  </div>
                  <div className="mt-2 h-1.5 bg-canvas2 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${c.progress_pct}%`, background: "var(--accent-blue)" }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>
      <BottomNav />
    </div>
  );
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

function SummaryCard({ label, value, unit, onClick, testid }) {
  const isButton = typeof onClick === "function";
  const commonClass =
    "bg-white rounded-[16px] p-3.5 border border-line2 text-left transition active:scale-[0.98]";
  const inner = (
    <>
      <div className="text-[11px] uppercase tracking-wider text-ink-muted">{label}</div>
      <div className="mt-1 flex items-baseline gap-1 min-w-0">
        <span className="font-display text-[24px] leading-none">{value}</span>
        <span className="text-[12px] text-ink-muted truncate">{unit}</span>
      </div>
    </>
  );
  if (isButton) {
    return (
      <button
        type="button"
        onClick={onClick}
        data-testid={testid}
        className={commonClass}
        style={{ boxShadow: "var(--shadow-soft)" }}
      >
        {inner}
      </button>
    );
  }
  return (
    <div className={commonClass} style={{ boxShadow: "var(--shadow-soft)" }} data-testid={testid}>
      {inner}
    </div>
  );
}

function QuickTile({ to, img, label, icon: Icon }) {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(to)}
      data-testid={`quick-${label.toLowerCase().replace(/\s/g, "-")}`}
      className="relative h-[120px] rounded-[16px] overflow-hidden text-left"
    >
      <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.65) 100%)" }} />
      <div className="absolute top-3 left-3 w-8 h-8 rounded-full grid place-items-center bg-white/90 backdrop-blur">
        <Icon size={16} />
      </div>
      <div className="absolute bottom-3 left-3 text-white font-display text-[16px]">{label}</div>
    </button>
  );
}
